/**
 * ============================================================
 *  Rascunho e fila de reenvio offline — via IndexedDB
 * ============================================================
 *
 *  Por quê: localStorage tem um teto de ~5-10MB por site — suficiente
 *  pra texto, mas aperta rápido com várias fotos/vídeos em base64.
 *  IndexedDB não tem esse teto apertado e foi feito pra guardar
 *  blocos grandes de dados no próprio navegador, sem servidor.
 *
 *  Pensado pra ser reaproveitado por qualquer sala com formulário
 *  longo + mídia (não só a Expedição) — por isso as funções recebem
 *  a "chave" de fora, sem nada específico de uma sala só.
 *
 *  Dois armazéns (object stores):
 *    - "rascunhos": progresso de um formulário em andamento, uma
 *      entrada por chave (ex: "sala1:12345:cadeira-gama-plus").
 *    - "pendentes": payloads finais prontos pra enviar, guardados
 *      quando o envio falha por falta de conexão — cada um vira uma
 *      entrada própria (não substitui, se acumula até reenviar).
 * ============================================================
 */

const NOME_BANCO = 'sgq_offline';
/* Versão 2 (17/08/2026): a store `fotos_pendentes` só é criada se a
   versão subir — o `onupgradeneeded` não roda de novo na mesma versão, e
   quem já tem o banco ficaria sem ela para sempre. */
const VERSAO_BANCO = 2;

const abrirBanco = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(NOME_BANCO, VERSAO_BANCO);
  req.onupgradeneeded = () => {
    const db = req.result;
    if (!db.objectStoreNames.contains('rascunhos')) db.createObjectStore('rascunhos');
    if (!db.objectStoreNames.contains('pendentes')) db.createObjectStore('pendentes', { autoIncrement: true });
    /* FOTOS QUE AINDA NÃO SUBIRAM (17/08/2026) — a fila `pendentes`
       guarda conferências CONCLUÍDAS. Faltava o caso de tirar foto sem
       sinal e deixar como rascunho: elas ficavam só no aparelho, e um
       celular trocado ou app reinstalado as levaria junto.

       As que já subiram estão no servidor; estas não existem em lugar
       nenhum. */
    if (!db.objectStoreNames.contains('fotos_pendentes')) db.createObjectStore('fotos_pendentes', { autoIncrement: true });
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const comLoja = async (nomeLoja, modo, fn) => {
  const db = await abrirBanco();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(nomeLoja, modo);
    const loja = tx.objectStore(nomeLoja);
    const resultadoReq = fn(loja);
    tx.oncomplete = () => resolve(resultadoReq?.result);
    tx.onerror = () => reject(tx.error);
  });
};

// ── Rascunho (uma entrada por chave, sempre sobrescreve) ────
export const salvarRascunho = (chave, dados) =>
  comLoja('rascunhos', 'readwrite', loja => loja.put({ ...dados, salvoEm: new Date().toISOString() }, chave));

export const buscarRascunho = (chave) =>
  comLoja('rascunhos', 'readonly', loja => loja.get(chave));

/* (V1905) LISTAR OS RASCUNHOS DE UM PREFIXO — a Sala 5 guarda as fichas dela com chaves próprias ("sala5:ficha:…", "sala5:laudo:…") e precisa
   achá-las todas (o post-it "neste aparelho" e o laudo guardado que vai sozinho). Só lê; não mexe em nada das outras salas. */
export const listarRascunhos = async (prefixo) => {
  const db = await abrirBanco();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('rascunhos', 'readonly'); const out = [];
    const req = tx.objectStore('rascunhos').openCursor(IDBKeyRange.bound(prefixo, prefixo + '\uffff'));
    req.onsuccess = () => { const c = req.result; if (c) { out.push({ chave: c.key, ...c.value }); c.continue(); } };
    tx.oncomplete = () => resolve(out); tx.onerror = () => reject(tx.error);
  });
};
export const removerRascunho = (chave) =>
  comLoja('rascunhos', 'readwrite', loja => loja.delete(chave));

// ── Pendentes de envio (várias entradas, uma por tentativa) ─
/* GUARDA O TOKEN JUNTO (17/08/2026) — o Service Worker reenvia esta
   fila em segundo plano, e ele NÃO acessa localStorage. Sem o token
   guardado aqui, o reenvio sairia sem `Authorization` e voltaria 401
   para sempre.

   O token fica no IndexedDB do próprio aparelho, ao lado da conferência
   com as fotos — mesmo dado, mesmo lugar, nenhuma exposição nova.

   E PEDE O SYNC: é isso que faz o navegador acordar o worker quando a
   rede voltar, mesmo com a aba fechada. Falhando (iOS, ou permissão
   negada), o comportamento antigo continua — reenvio ao abrir o
   sistema. */
export const salvarPendente = async (payload) => {
  const token = localStorage.getItem('sgq_token');
  await comLoja('pendentes', 'readwrite', loja => loja.add({ payload, token, criado_em: Date.now() }));
  try {
    const reg = await navigator.serviceWorker?.ready;
    await reg?.sync?.register('sgq-enviar-conferencias');
  } catch (e) {
    console.error('[rascunhoOffline/sync]', e.message);
  }
};

export const listarPendentes = async () => {
  const db = await abrirBanco();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendentes', 'readonly');
    const loja = tx.objectStore('pendentes');
    const chaves = [];
    const cursorReq = loja.openCursor();
    cursorReq.onsuccess = (e) => {
      const cursor = e.target.result;
      /* `valor` e não `payload`: desde 17/08/2026 cada item é
         `{ payload, token }` — o token existe para o Service Worker
         reenviar em segundo plano. Nomear de `payload` aqui faria quem
         lê supor que é o corpo da requisição. */
      if (cursor) { chaves.push({ chave: cursor.key, valor: cursor.value }); cursor.continue(); }
      else resolve(chaves);
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
};

/* Grava o progresso: o payload volta para a fila com os caminhos das
   fotos que já subiram. Se o envio final falhar, a próxima tentativa não
   reenvia nenhuma delas.

   Sem isto, 19 fotos subidas e a 20ª falhando faria tudo recomeçar — e
   numa rede ruim isso é a diferença entre concluir e nunca concluir. */
export const atualizarPendente = (chave, valorNovo) =>
  comLoja('pendentes', 'readwrite', loja => loja.put(valorNovo, chave));

export const removerPendente = (chave) =>
  comLoja('pendentes', 'readwrite', loja => loja.delete(chave));

export const contarPendentes = async () => (await listarPendentes()).length;


/* ══ FILA DE FOTOS DE RASCUNHO (17/08/2026) ══════════════════════════
   Cada item leva tudo que o servidor precisa para guardar a foto na
   pasta certa do rascunho — e o token, porque quem reenvia é o Service
   Worker, que não acessa localStorage. */
export const salvarFotoPendente = async ({ chaveRascunho, idMidia, base64, nomeArquivo, tipo }) => {
  const token = localStorage.getItem('sgq_token');
  await comLoja('fotos_pendentes', 'readwrite', loja =>
    loja.add({ chaveRascunho, idMidia, base64, nomeArquivo, tipo, token, criado_em: Date.now() }));
  try {
    const reg = await navigator.serviceWorker?.ready;
    await reg?.sync?.register('sgq-enviar-fotos');
  } catch (e) {
    console.error('[rascunhoOffline/syncFotos]', e.message);
  }
};

export const listarFotosPendentes = async () => {
  const db = await abrirBanco();
  return new Promise((resolve) => {
    const tx = db.transaction('fotos_pendentes', 'readonly');
    const req = tx.objectStore('fotos_pendentes').openCursor();
    const itens = [];
    req.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return resolve(itens);
      itens.push({ chave: c.key, valor: c.value });
      c.continue();
    };
    req.onerror = () => resolve(itens);
  });
};

export const removerFotoPendente = (chave) =>
  comLoja('fotos_pendentes', 'readwrite', loja => loja.delete(chave));

export const contarFotosPendentes = async () => (await listarFotosPendentes()).length;


/* ══ SUBIR AS FOTOS ANTES DE REENVIAR (19/08/2026) ═══════════════════
   O caminho estrutural, pedido pelo Juan: "quero resolver de vez, sem
   perder foto e formulário".

   O PROBLEMA QUE ISTO RESOLVE: a conferência offline guardava as fotos
   em base64 DENTRO do payload. Vinte fotos viram 33 MB num corpo só, e
   qualquer limite de proxy no caminho derruba o envio inteiro — perdendo
   tudo por causa do tamanho, não do conteúdo.

   Com rede, o sistema nunca faz isso: cada foto sobe sozinha, e o envio
   final leva só os CAMINHOS. Aqui o reenvio passa a fazer igual.

   O GANHO NÃO É SÓ O TAMANHO: subindo uma a uma, uma falha no meio custa
   UMA FOTO, não a conferência inteira — e as que já subiram continuam no
   servidor para a próxima tentativa. O envio deixa de ser tudo-ou-nada.

   E a qualidade não muda: a foto vai como foi tirada (2000px, 0,90), sem
   nenhuma compressão adicional. O que muda é COMO ela viaja. */
export const subirMidiasDoPayload = async (API, payload, chaveRascunho) => {
  const subirUma = async (m, tipo) => {
    /* Já tem caminho: subiu antes (nesta ou noutra tentativa). Não
       reenvia — é o que torna a retomada barata. */
    if (m.caminho_temp) return m;
    /* ⚠ SEM CAMINHO E SEM DADOS É PERDA, E ANTES PASSAVA EM SILÊNCIO
       (08/09/2026, achado ao investigar o pedido de revisar as fotos do
       envio offline).

       A guarda era `if (m.caminho_temp || !m.base64) return m;`. O
       segundo termo existia para não tentar subir o que não tem dados —
       mas ele também casava no caso em que a mídia perdeu o base64 SEM
       ter subido: o IndexedDB limpo com o aparelho cheio, que é
       exatamente o que a faixa da fila avisa que pode acontecer.

       Nesse caso a função devolvia o item COMO SE ESTIVESSE PRONTO, e
       ele viajava até o servidor vazio. Lá o `criarRegistro` registra
       `VAZIO → FALHOU` e dispara o `[Sala1/INTEGRIDADE]` — ou seja, a
       detecção sempre funcionou de ponta a ponta. O que não funcionava
       era a notícia sair do log.

       Falhar aqui é melhor que seguir: a conferência fica na fila, com
       as outras mídias preservadas, em vez de virar um registro
       incompleto que ninguém sabe que está incompleto. */
    if (!m.base64) {
      const err = new Error(`Mídia sem arquivo: ${m.nome_arquivo || tipo}`);
      err.midiaPerdida = true;
      throw err;
    }
    const r = await API.post(`/sala1/rascunho/${encodeURIComponent(chaveRascunho)}/arquivo`, {
      dados_base64: m.base64,
      nome_arquivo: m.nome_arquivo,
      tipo,
    });
    return {
      caminho_temp: r.data.caminho,
      pasta_temp: r.data.pasta_temp,
      tipo: m.tipo || tipo,
      nome_arquivo: m.nome_arquivo,
      orientacao_id: m.orientacao_id,
    };
  };

  /* ⚠ O PROGRESSO PARCIAL PRECISA SOBREVIVER À FALHA (08/09/2026).

     O comentário acima prometia: "falhando na 20ª, as 19 que subiram não
     são reenviadas na próxima tentativa". **O código não fazia isso.**
     O laço não tinha `try`, então a primeira foto que falhava derrubava
     a função inteira — e quem chama só grava o progresso DEPOIS do
     retorno. Como ela lançava, o `atualizarPendente` nunca era
     alcançado, e os `caminho_temp` conquistados viviam só no objeto em
     memória, descartado junto com o erro.

     Efeito real no galpão: conexão instável com 20 fotos podia nunca
     fechar. Cada tentativa refazia o trabalho todo, falhava no mesmo
     ponto, e o servidor acumulava os temporários de cada rodada.

     ⚠ O TESTE NÃO PEGAVA, e vale entender por quê: ele cobria
     `atualizarPendente` sozinho (a camada sabe guardar) e esta função
     com `caminho_temp` já presente (ela não reenvia o que subiu).
     Nenhum dos dois cobria o CALLER com falha no meio — que era o elo
     que faltava. Peças verdes, cadeia quebrada.

     Agora o que já subiu viaja NO PRÓPRIO ERRO (`err.parcial`), e quem
     chama grava antes de desistir da rodada. */
  const fotos = [];
  const videos = [];
  const parcial = () => ({ ...payload, fotos: [...fotos, ...(payload.fotos || []).slice(fotos.length)],
                                       videos: [...videos, ...(payload.videos || []).slice(videos.length)] });
  try {
    for (const f of (payload.fotos || [])) fotos.push(await subirUma(f, 'foto'));
    for (const v of (payload.videos || [])) videos.push(await subirUma(v, 'video'));
  } catch (e) {
    e.parcial = parcial();
    throw e;
  }

  return { ...payload, fotos, videos };
};

/* ══ O LOGIN DA FILA ACOMPANHA O DA PESSOA (o envio que não perde foto) ══════════════════════════════════════════════
   REGRA: quem reenvia em segundo plano é o service worker, que não lê o localStorage: cada item da fila guarda o login do
   momento em que entrou. Quando ele vence, o envio volta 401 e o item espera. Aqui, sempre que a pessoa entra ou o login se
   renova, os itens DELA recebem o login novo, e o envio é pedido na hora.
   ⚠ ARMADILHA: o celular da expedição é compartilhado. Só recebe o login novo o item cujo login antigo é da MESMA pessoa
   (o id de dentro do login); o de outra pessoa espera ela entrar. Entregar o login de um ao item de outro faria a foto subir
   na pasta e no nome de quem não a tirou. */
const idDoLogin = (token) => {
  try { return JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).id ?? null; }
  catch { return null; }
};

export const atualizarTokenDasFilas = async (tokenNovo) => {
  const dono = idDoLogin(tokenNovo);
  if (!dono) return;
  let trocados = 0;
  try {
    const db = await abrirBanco();
    for (const nomeLoja of ['pendentes', 'fotos_pendentes']) {
      if (!db.objectStoreNames.contains(nomeLoja)) continue;
      await new Promise((ok) => {
        const tx = db.transaction(nomeLoja, 'readwrite');
        const req = tx.objectStore(nomeLoja).openCursor();
        req.onsuccess = (e) => {
          const c = e.target.result;
          if (!c) return;
          const v = c.value;
          if (v && v.token && v.token !== tokenNovo && String(idDoLogin(v.token)) === String(dono)) {
            c.update({ ...v, token: tokenNovo, aguardaLogin: false });
            trocados++;
          }
          c.continue();
        };
        tx.oncomplete = () => ok();
        tx.onerror = () => ok();
      });
    }
  } catch (e) {
    console.error('[rascunhoOffline/loginDaFila]', e.message);
    return;
  }
  if (trocados) navigator.serviceWorker?.controller?.postMessage('sgq-tentar-envio');
};

/* ══ O CORPO DA CONCLUSÃO QUE VAI PARA O SERVIDOR (o envio que não perde foto) ═══════════════════════════════════════
   REGRA: a fila guarda cada foto INTEIRA junto do caminho (a pasta temporária onde ela já subiu), até o servidor aceitar a
   conferência. O que viaja é leve: a foto que já subiu vai só pelo caminho. A inteira só vai para as `inteiras` (as que o
   servidor disse não ter mais, no 422).
   ⚠ O service worker tem uma cópia desta regra (paraEnvio, em public/sw.js): ele não importa módulos. Mudou aqui, muda lá. */
export const paraEnvio = (corpo, inteiras = null) => {
  const um = (m) => {
    if (!m.caminho_temp) return m;
    if (inteiras && inteiras.has(m.caminho_temp) && m.base64) { const { caminho_temp, pasta_temp, ...resto } = m; return resto; }
    const { base64, ...resto } = m; return resto;
  };
  return { ...corpo, fotos: (corpo.fotos || []).map(um), videos: (corpo.videos || []).map(um) };
};

/* Manda a conclusão guardada na fila: leve primeiro; se o servidor disser que falta o arquivo de alguma foto (422) e a fila
   tem a foto inteira, manda de novo com ela. Sem a foto inteira, o erro sobe (a conferência fica na fila). */
export const enviarConclusao = async (API, corpo) => {
  try {
    return await API.post('/sala1', paraEnvio(corpo));
  } catch (e) {
    const faltam = e.response?.status === 422 ? (e.response.data?.faltam || []).map(f => f.caminho_temp) : null;
    if (!faltam) throw e;
    const todas = [...(corpo.fotos || []), ...(corpo.videos || [])];
    if (!faltam.every(c => todas.some(m => m.caminho_temp === c && m.base64))) throw e;
    return API.post('/sala1', paraEnvio(corpo, new Set(faltam)));
  }
};
